import { loadQuery } from 'react-relay';

import { getAccountId, getUserId } from 'lib/utils/authentication';
import RelayEnvironment from 'lib/utils/relay';
import { CONTACT_QUERY } from 'services/contact/contact.relay.services';
import { contactQuery } from 'services/contact/__generated__/contactQuery.graphql';
import { COUNTRIES_QUERY } from 'services/country/country.relay.services';
import { countryCountriesQuery } from 'services/country/__generated__/countryCountriesQuery.graphql';
import { FEES_QUERY } from 'services/fee/fee.relay.services';
import { feesQuery } from 'services/fee/__generated__/feesQuery.graphql';
import { TEAMS_QUERY } from 'services/team/team.relay.services';
import { USER_PROFILE_QUERY } from 'services/user/user.relay.services';
import { userProfileQuery } from 'services/user/__generated__/userProfileQuery.graphql';
import { teamsQuery } from 'services/team/__generated__/teamsQuery.graphql';
import { eventsQuery } from 'services/event/__generated__/eventsQuery.graphql';
import { EVENTS_QUERY } from 'services/event/event.relay.services';
interface Props {
  clientId: string;
}

export const ClientProfilePreloadData = ({ clientId }: Props) => {
  const userId = getUserId();

  return {
    userProfileQueryRef: loadQuery<userProfileQuery>(RelayEnvironment, USER_PROFILE_QUERY, {
      id: userId,
      skip: userId === '',
    }),
    contactQueryRef: loadQuery<contactQuery>(RelayEnvironment, CONTACT_QUERY, {
      id: clientId,
    }),
    countriesQueryRef: loadQuery<countryCountriesQuery>(
      RelayEnvironment,
      COUNTRIES_QUERY,
      {
        first: 1000,
      },
      { fetchPolicy: 'store-or-network' },
    ),
    teamsQueryRef: loadQuery<teamsQuery>(
      RelayEnvironment,
      TEAMS_QUERY,
      {
        userId: userId,
        skip: false,
      },
      { fetchPolicy: 'store-or-network' },
    ),
    feesQueryRef: loadQuery<feesQuery>(
      RelayEnvironment,
      FEES_QUERY,
      {
        first: 100,
        filter: {
          where: {
            accountId: { equalTo: getAccountId() },
            deletedAt: { isNull: true },
          },
        },
      },
      { fetchPolicy: 'store-or-network' },
    ),
    upcomingEventsQueryRef: loadQuery<eventsQuery>(
      RelayEnvironment,
      EVENTS_QUERY,
      {
        first: 50,
        ordering: [{ sort: 'BEGINS_AT', direction: 'ASC' }],
        filter: {
          where: {
            userId: { equalTo: userId },
            deletedAt: { isNull: true },
            status: { in: ['draft', 'published', 'completed', 'incomplete', 'cancelled'] },
            beginsAt: { moreThanOrEqualTo: new Date() },
            eventGuests: {
              contact: {
                id: { equalTo: clientId },
              },
            },
          },
        },
        skip: false,
      },
      { fetchPolicy: 'store-or-network' },
    ),
    pastEventsQueryRef: loadQuery<eventsQuery>(
      RelayEnvironment,
      EVENTS_QUERY,
      {
        first: 50,
        ordering: [{ sort: 'BEGINS_AT', direction: 'DESC' }],
        filter: {
          where: {
            userId: { equalTo: userId },
            deletedAt: { isNull: true },
            status: { in: ['draft', 'published', 'completed', 'incomplete', 'cancelled'] },
            beginsAt: { lessThan: new Date() },
            eventGuests: {
              contact: {
                id: { equalTo: clientId },
              },
            },
          },
        },
        skip: false,
      },
      { fetchPolicy: 'store-or-network' },
    ),
  };
};

export type ClientProfilePreloadDataType = ReturnType<typeof ClientProfilePreloadData>;
